import { ThreeEvent } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { ModelInModelTree } from '../model-space';
import { exportToGLTFAndLoadModel } from '../../../utils/export-to-gltf-and-load-model/export-to-gltf-and-load-model';
import { createModelTreeInModelSpace } from '../../../utils/create-model-tree-in-model-space/create-model-tree-in-model-space';

type ModelProps = {
    id?: number;
    uuid: string;
    name: string;
    visible: boolean;
    position: THREE.Vector3Tuple;
    rotation: THREE.EulerTuple;
    scale: THREE.Vector3Tuple;
    active?: boolean;
    model?: string | File;
    onClick?: (event: ThreeEvent<MouseEvent>) => void;
    onDoubleClick?: (event: ThreeEvent<MouseEvent>) => void;
    onPointerMissed?: ((event: MouseEvent) => void);
    onModelLoaded?: (uuid: string, successfullyLoaded: boolean, children?: ModelInModelTree[]) => void;
};

export const Model = ({
    id,
    uuid,
    name,
    visible,
    position,
    rotation,
    scale,
    active,
    model,
    onClick,
    onDoubleClick,
    onPointerMissed,
    onModelLoaded
}: ModelProps) => {
    const groupRef = useRef<THREE.Group<THREE.Object3DEventMap>>();
    const [loadedModel, setLoadedModel] = useState<THREE.Group<THREE.Object3DEventMap> | THREE.Object3D>();
    const ignoreUUIDsRef = useRef<string[]>();
    const [prevModel, setPrevModel] = useState<typeof model>();

    useEffect(() => {
        return () => {
            if (ignoreUUIDsRef.current) ignoreUUIDsRef.current.push(uuid);
        };
    }, [uuid]);

    if (model !== prevModel) {
        setPrevModel(model);

        if (!model) return null;

        if (typeof model === 'string') { // URL
            const extension = model.split('.').pop()?.toLowerCase();
            
            switch (extension) {
                case '3dm': {
                    const loadModel = async () => {
                        const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');
                        const loader = new Rhino3dmLoader();
                        loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                        loader.load(model, async (object) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();
                    break;
                }
                case 'glb':
                case 'gltf': {
                    const loadModel = async () => {
                        const manager = new THREE.LoadingManager();

                        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                        const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                        const dracoLoader = new DRACOLoader();
                        dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                        const ktx2Loader = new KTX2Loader(manager);
                        ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                        const loader = new GLTFLoader(manager);
                        loader.setDRACOLoader(dracoLoader);
                        loader.setKTX2Loader(ktx2Loader);
                        loader.setMeshoptDecoder(MeshoptDecoder);

                        loader.load(model, (gltf) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            if (onModelLoaded)
                                onModelLoaded(uuid, true, gltf.scene?.children ? createModelTreeInModelSpace(gltf.scene.children) : []);

                            //centerAllMeshesIndividually(gltf.scene);

                            setLoadedModel(gltf.scene);
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();

                    break;
                }
                case 'obj': {
                    const loadModel = async () => {
                        const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');

                        const loader = new OBJLoader();
                        
                        loader.load(model, async (object) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();
                    break;
                }
                case 'drc': {
                    const loadModel = async () => {
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                        const loader = new DRACOLoader();
                        loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                        loader.load(model, async (geometry) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            let object;

                            if ( geometry.index !== null ) {

                                const material = new THREE.MeshStandardMaterial();

                                object = new THREE.Mesh(geometry, material);
                                object.name = 'drcModell';

                            } else {

                                const material = new THREE.PointsMaterial({ size: 0.01 });
                                material.vertexColors = geometry.hasAttribute('color');

                                object = new THREE.Points(geometry, material);
                                object.name = 'drcModell';

                            }

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();
                    
                    break;
                }
                case 'fbx': {
                    const loadModel = async () => {
                        const manager = new THREE.LoadingManager();
                        const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                        const loader = new FBXLoader(manager);

                        loader.load(model, async (object) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            object.traverse(object => {
                                const _object: any = object;
                                if (_object.material) {
                                    _object.material = new THREE.MeshStandardMaterial({
                                        color: _object.material.color
                                    });
                                }
                            });

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();

                    break;
                }
                //case 'max':
                case '3ds': {
                    const loadModel = async () => {
                        const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                        const loader = new TDSLoader();

                        loader.load(model, async (object) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, undefined, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    };
                    loadModel();

                    break;
                }
            }

        } else { // File
            const extension = model.name.split('.').pop()?.toLowerCase();
            
            switch (extension) {
                case '3dm': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const { Rhino3dmLoader } = await import('three/addons/loaders/3DMLoader.js');

                        const loader = new Rhino3dmLoader();
                        loader.setLibraryPath(`${process.env.REACT_APP_ORIGIN}/libs/rhino3dm/`);

                        loader.parse(event.target?.result as ArrayBufferLike , async (object) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    }, false);
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'glb':
                case 'gltf': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const manager = new THREE.LoadingManager();

                        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
                        const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
                        const { MeshoptDecoder } = await import('three/addons/libs/meshopt_decoder.module.js');

                        const dracoLoader = new DRACOLoader();
                        dracoLoader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/gltf/`);

                        const ktx2Loader = new KTX2Loader(manager);
                        ktx2Loader.setTranscoderPath(`${process.env.REACT_APP_ORIGIN}/libs/basis/`);

                        const loader = new GLTFLoader(manager);
                        loader.setDRACOLoader(dracoLoader);
                        loader.setKTX2Loader(ktx2Loader);
                        loader.setMeshoptDecoder(MeshoptDecoder);

                        loader.parse(event.target?.result!, '', (gltf) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            loader.dracoLoader?.dispose();
                            loader.ktx2Loader?.dispose();

                            //gltf.scene.position.set(0, 0, 0);

                            if (onModelLoaded)
                                onModelLoaded(uuid, true, gltf.scene?.children ? createModelTreeInModelSpace(gltf.scene.children) : []);

                            setLoadedModel(gltf.scene);
                        }, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case 'obj': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js');
                        const loader = new OBJLoader();

                        const object = loader.parse(event.target?.result as string);

                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const gltfModel = await exportToGLTFAndLoadModel(object);

                        if (gltfModel) {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                            }

                            setLoadedModel(gltfModel.scene);
                        } else {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, false);
                            }
                        }
                    }, false);
                    fileReader.readAsText(model);

                    break;
                }
                case 'drc': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');

                        const loader = new DRACOLoader();
                        loader.setDecoderPath(`${process.env.REACT_APP_ORIGIN}/libs/draco/`);

                        loader.parse(event.target?.result as ArrayBuffer, async (geometry) => {
                            if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                            let object;

                            if ( geometry.index !== null ) {

                                const material = new THREE.MeshStandardMaterial();

                                object = new THREE.Mesh(geometry, material);
                                object.name = 'drcModell';

                            } else {

                                const material = new THREE.PointsMaterial({ size: 0.01 });
                                material.vertexColors = geometry.hasAttribute('color');

                                object = new THREE.Points(geometry, material);
                                object.name = 'drcModell';

                            }

                            const gltfModel = await exportToGLTFAndLoadModel(object);

                            if (gltfModel) {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                                }

                                setLoadedModel(gltfModel.scene);
                            } else {
                                if (onModelLoaded) {
                                    onModelLoaded(uuid, false);
                                }
                            }
                        }, () => {
                            if (onModelLoaded) onModelLoaded(uuid, false);
                        });
                    }, false);
                    fileReader.readAsArrayBuffer(model);
                    
                    break;
                }
                case 'fbx': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const manager = new THREE.LoadingManager();
                        const { FBXLoader } = await import('three/addons/loaders/FBXLoader.js');

                        const loader = new FBXLoader(manager);

                        const object = loader.parse(event.target?.result!, '');

                        object.traverse(object => {
                            const _object: any = object;
                            if (_object.material) {
                                _object.material = new THREE.MeshStandardMaterial({
                                    color: _object.material.color
                                });
                            }
                        });

                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const gltfModel = await exportToGLTFAndLoadModel(object);

                        if (gltfModel) {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                            }

                            setLoadedModel(gltfModel.scene);
                        } else {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, false);
                            }
                        }
                        
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
                case '3ds': {
                    const fileReader = new FileReader();

                    fileReader.addEventListener('load', async (event) => {
                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const { TDSLoader } = await import('three/addons/loaders/TDSLoader.js');
                        const loader = new TDSLoader();

                        const object = loader.parse(event.target?.result as ArrayBuffer, '');

                        if (ignoreUUIDsRef.current && ignoreUUIDsRef.current.includes(uuid)) return;

                        const gltfModel = await exportToGLTFAndLoadModel(object);

                        if (gltfModel) {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, true, gltfModel.scene.children ? createModelTreeInModelSpace(gltfModel.scene.children) : []);
                            }

                            setLoadedModel(gltfModel.scene);
                        } else {
                            if (onModelLoaded) {
                                onModelLoaded(uuid, false);
                            }
                        }
                    });
                    fileReader.readAsArrayBuffer(model);

                    break;
                }
            }
        }
    }

    return (
        <group
            uuid={uuid}
            visible={visible}
            ref={groupRef as React.Ref<THREE.Group<THREE.Object3DEventMap>>}
            position={position}
            rotation={rotation}
            scale={scale}
            onClick={onClick}
            onPointerMissed={onPointerMissed}
            onDoubleClick={onDoubleClick}
        >
            { loadedModel && <primitive object={loadedModel} /> }
        </group>
    );
};